import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../context/AppContext';

const EstimateModal = ({ rooms, onClose }) => {
  const canvasRef = useRef(null);
  const [canvasDataUrl, setCanvasDataUrl] = useState('');
  const { canvas, stage, shapes } = useApp();

  // Capture canvas image on mount
  useEffect(() => {
    const captureCanvas = () => {
      console.log('🎯 Starting canvas capture...', { hasCanvas: !!canvas, hasStage: !!stage });
      
      // Method 1: Try using PIXI renderer extract (most reliable for PIXI)
      if (stage?.app?.renderer?.extract) {
        try {
          console.log('📸 Attempting PIXI renderer extract...');
          
          // Use PIXI's built-in extract functionality
          const renderTexture = stage.app.renderer.generateTexture(stage.app.stage);
          const canvas = stage.app.renderer.extract.canvas(renderTexture);
          const dataUrl = canvas.toDataURL('image/png', 1.0);
          
          setCanvasDataUrl(dataUrl);
          console.log('✅ PIXI renderer extract successful!', {
            width: canvas.width,
            height: canvas.height,
            dataUrlLength: dataUrl.length
          });
          
          // Clean up the render texture
          renderTexture.destroy();
          return;
        } catch (error) {
          console.warn('❌ PIXI renderer extract failed:', error);
        }
      }
      
      // Method 2: Try direct canvas extraction from context
      if (canvas) {
        try {
          console.log('📸 Attempting direct canvas extraction...');
          
          // For WebGL, we need to ensure the drawing buffer is preserved
          const gl = canvas.getContext('webgl') || canvas.getContext('webgl2') || canvas.getContext('experimental-webgl');
          
          if (gl) {
            console.log('🔧 WebGL context detected, checking preserveDrawingBuffer...');
            
            // Check if preserveDrawingBuffer is enabled
            const contextAttributes = gl.getContextAttributes();
            if (!contextAttributes.preserveDrawingBuffer) {
              console.warn('⚠️ preserveDrawingBuffer is disabled - canvas may appear black');
            }
          }
          
          // Create a new canvas to copy the data
          const tempCanvas = document.createElement('canvas');
          const tempCtx = tempCanvas.getContext('2d');
          
          tempCanvas.width = canvas.width;
          tempCanvas.height = canvas.height;
          
          // Fill with white background
          tempCtx.fillStyle = '#ffffff';
          tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
          
          // Draw the PIXI canvas
          tempCtx.drawImage(canvas, 0, 0);
          
          const dataUrl = tempCanvas.toDataURL('image/png', 1.0);
          setCanvasDataUrl(dataUrl);
          
          console.log('✅ Direct canvas extraction successful!', {
            width: tempCanvas.width,
            height: tempCanvas.height,
            dataUrlLength: dataUrl.length
          });
          return;
        } catch (error) {
          console.warn('❌ Direct canvas extraction failed:', error);
        }
      }
      
      // Method 3: Fallback to querySelector with better selectors
      const selectors = [
        'canvas[data-pixi="true"]',
        '.pixi-canvas canvas',
        'canvas',
        '[data-app="pixi"] canvas'
      ];
      
      for (const selector of selectors) {
        const foundCanvas = document.querySelector(selector);
        if (foundCanvas) {
          try {
            console.log(`📸 Found canvas with selector "${selector}", attempting extraction...`);
            
            const tempCanvas = document.createElement('canvas');
            const tempCtx = tempCanvas.getContext('2d');
            
            tempCanvas.width = foundCanvas.width;
            tempCanvas.height = foundCanvas.height;
            
            tempCtx.fillStyle = '#ffffff';
            tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
            tempCtx.drawImage(foundCanvas, 0, 0);
            
            const dataUrl = tempCanvas.toDataURL('image/png', 1.0);
            setCanvasDataUrl(dataUrl);
            
            console.log('✅ Fallback canvas extraction successful!', {
              selector,
              width: tempCanvas.width,
              height: tempCanvas.height,
              dataUrlLength: dataUrl.length
            });
            return;
          } catch (error) {
            console.warn(`❌ Fallback extraction failed for selector "${selector}":`, error);
          }
        }
      }
      
      // Method 4: Force PIXI to render to a new texture
      if (stage?.app?.stage) {
        try {
          console.log('📸 Attempting force render to texture...');
          
          const app = stage.app;
          const renderTexture = app.renderer.generateTexture(app.stage, {
            scaleMode: 0, // Linear scaling
            resolution: 1,
            region: null
          });
          
          // Extract to canvas
          const extractedCanvas = app.renderer.extract.canvas(renderTexture);
          const dataUrl = extractedCanvas.toDataURL('image/png', 1.0);
          
          setCanvasDataUrl(dataUrl);
          console.log('✅ Force render successful!', {
            width: extractedCanvas.width,
            height: extractedCanvas.height,
            dataUrlLength: dataUrl.length
          });
          
          renderTexture.destroy();
          return;
        } catch (error) {
          console.warn('❌ Force render failed:', error);
        }
      }
      
      console.error('🚨 All canvas capture methods failed');
    };

    // Multiple capture attempts with different timings
    const attemptCapture = () => {
      captureCanvas();
      if (!canvasDataUrl) {
        setTimeout(captureCanvas, 100);
        setTimeout(captureCanvas, 500);
        setTimeout(captureCanvas, 1000);
      }
    };

    attemptCapture();
  }, [canvas, stage, canvasDataUrl]);

  // Analyze door shapes for carpet calculation adjustments
  const analyzeDoorShapes = (room, shapes) => {
    let doorShapes = [];
    let totalDoorArea = 0;
    let doorCount = 0;
    
    // Find all door shapes in this room's area or snapped to this room
    if (room.type === 'individual' && room.originalShape) {
      // For individual rooms, check for doors that overlap with the room OR are snapped to it
      doorShapes = shapes.filter(shape => 
        shape.type === 'door' && (
          isShapeInRoom(shape, room.originalShape) || 
          shape.snappedToRoom === room.originalShape.id
        )
      );
    } else if (room.type === 'merged' && room.shapes) {
      // For merged rooms, check for doors in the bounding area OR snapped to any room in the structure
      const roomIds = room.shapes.map(s => s.id);
      doorShapes = shapes.filter(shape => 
        shape.type === 'door' && (
          isShapeInMergedRoom(shape, room.shapes) || 
          roomIds.includes(shape.snappedToRoom)
        )
      );
    }
    
    doorShapes.forEach(door => {
      const doorArea = calculateDoorArea(door);
      totalDoorArea += doorArea;
      doorCount++;
    });
    
    return {
      doorCount,
      totalDoorArea,
      doorShapes,
      hasComplexDoorCuts: doorShapes.some(door => door.radius > 80), // Large doors need complex cuts
      needsTransitionStrips: doorCount > 0,
      doorWasteAdjustment: doorCount * 0.02, // 2% waste per door for complex cuts
      hasSnappedDoors: doorShapes.some(door => door.snappedToRoom),
      snappedDoorsCount: doorShapes.filter(door => door.snappedToRoom).length
    };
  };

  // Helper function to calculate door area
  const calculateDoorArea = (doorShape) => {
    const radius = doorShape.radius || 50;
    // Semi-circle area: (π × r²) / 2
    return (Math.PI * Math.pow(radius / 100, 2)) / 2; // Convert to square meters
  };

  // Helper function to check if door overlaps with room
  const isShapeInRoom = (doorShape, roomShape) => {
    const doorX = doorShape.x;
    const doorY = doorShape.y;
    const doorRadius = doorShape.radius || 50;
    
    const roomLeft = roomShape.x;
    const roomTop = roomShape.y;
    const roomRight = roomShape.x + (roomShape.width || roomShape.radius * 2);
    const roomBottom = roomShape.y + (roomShape.height || roomShape.radius * 2);
    
    // Check if door center or edges are within room bounds
    return (doorX >= roomLeft && doorX <= roomRight && doorY >= roomTop && doorY <= roomBottom) ||
           (doorX + doorRadius >= roomLeft && doorX + doorRadius <= roomRight && doorY >= roomTop && doorY <= roomBottom);
  };

  // Helper function to check if door is in merged room area
  const isShapeInMergedRoom = (doorShape, roomShapes) => {
    return roomShapes.some(shape => isShapeInRoom(doorShape, shape));
  };

  // Carpet calculation logic with enhanced industry standards
  const calculateCarpetEstimate = () => {
    // Safety check: ensure rooms is defined and is an array
    if (!rooms || !Array.isArray(rooms)) {
      console.error('❌ EstimateModal: rooms is not defined or not an array:', rooms);
      return [];
    }
    
    if (rooms.length === 0) {
      console.warn('⚠️ EstimateModal: rooms array is empty');
      return [];
    }
    
    const estimates = rooms.map(room => {
      const area = parseFloat(room.area);
      const width = parseFloat(room.width);
      const height = parseFloat(room.height);

      // Analyze door shapes in the room
      const doorAnalysis = analyzeDoorShapes(room, shapes);

      // Enhanced carpet roll analysis
      const standardWidths = [
        { width: 3.66, name: '12ft Roll', cost: 42 },
        { width: 4.0, name: '13ft Roll', cost: 45 },
        { width: 5.0, name: '16ft Roll', cost: 48 }
      ];
      
      // Determine optimal pile direction (usually toward main entrance or longest wall)
      const isWiderThanLong = width > height;
      const primaryDirection = isWiderThanLong ? 'width' : 'length';
      const pileDirection = isWiderThanLong ? 'Across width (toward entrance)' : 'Along length (toward entrance)';
      
      // Calculate for both orientations to find best option
      const orientations = [
        { name: 'Standard', dim1: width, dim2: height, pileDir: pileDirection },
        { name: 'Rotated 90°', dim1: height, dim2: width, pileDir: isWiderThanLong ? 'Along length' : 'Across width' }
      ];

      let bestOption = null;
      let minWaste = Infinity;
      let minCost = Infinity;

      orientations.forEach(orientation => {
        standardWidths.forEach(roll => {
          let analysis = null;

          if (orientation.dim1 <= roll.width) {
            // Single roll covers the width
            const rollsNeeded = 1;
            const lengthNeeded = orientation.dim2;
            const totalArea = roll.width * lengthNeeded;
            const wasteArea = totalArea - area;
            const wastePercentage = (wasteArea / area) * 100;
            const seamCount = 0;
            const totalCost = totalArea * roll.cost;

            analysis = {
              rollWidth: roll.width,
              rollName: roll.name,
              rollsNeeded,
              lengthNeeded,
              totalArea,
              wasteArea,
              wastePercentage,
              seamCount,
              seamLength: 0,
              orientation: orientation.name,
              pileDirection: orientation.pileDir,
              costPerSqm: roll.cost,
              materialCost: totalCost
            };
          } else {
            // Multiple rolls needed
            const rollsNeeded = Math.ceil(orientation.dim1 / roll.width);
            const lengthNeeded = orientation.dim2;
            const totalLength = lengthNeeded * rollsNeeded;
            const totalArea = roll.width * totalLength;
            const wasteArea = totalArea - area;
            const wastePercentage = (wasteArea / area) * 100;
            const seamCount = rollsNeeded - 1;
            const seamLength = seamCount * lengthNeeded;
            const totalCost = totalArea * roll.cost;

            analysis = {
              rollWidth: roll.width,
              rollName: roll.name,
              rollsNeeded,
              lengthNeeded,
              totalArea,
              wasteArea,
              wastePercentage,
              seamCount,
              seamLength,
              orientation: orientation.name,
              pileDirection: orientation.pileDir,
              costPerSqm: roll.cost,
              materialCost: totalCost
            };
          }

          // Prefer option with less waste, then less cost
          const score = analysis.wastePercentage + (analysis.materialCost / 1000); // Weight cost lower
          if (score < minWaste + (minCost / 1000)) {
            minWaste = analysis.wastePercentage;
            minCost = analysis.materialCost;
            bestOption = analysis;
          }
        });
      });

      // Add professional installation factors
      const installationWaste = 0.10; // 10% installation waste
      const doorAdjustments = room.type === 'merged' ? room.complexity * 0.02 : 0.02; // Door/threshold adjustments
      
      // Enhanced door-specific adjustments based on actual door shapes
      const doorSpecificAdjustments = doorAnalysis.doorWasteAdjustment + 
        (doorAnalysis.hasComplexDoorCuts ? 0.03 : 0); // Additional 3% for complex door cuts
      const totalDoorAdjustments = doorAdjustments + doorSpecificAdjustments;
      
      // Enhanced complexity factors for different room types
      let complexityFactor = 0;
      let shapeComplexitySurcharge = 0;
      
      if (room.type === 'merged') {
        complexityFactor = Math.min(room.complexity * 0.05, 0.15);
        
        // Additional factors for non-rectangular and curved shapes
        if (room.hasNonRectangular) {
          complexityFactor += 0.10; // 10% more for circles, triangles
          shapeComplexitySurcharge = area * 8; // $8 per sqm surcharge for complex cutting
        }
        
        if (room.hasCurvedRectangles) {
          complexityFactor += 0.08; // 8% more for curved rectangles
          shapeComplexitySurcharge += area * 6; // $6 per sqm surcharge for curved cutting
        }
      } else if (room.carpetComplexity === 'complex') {
        // Individual non-rectangular rooms
        if (room.shapeType === 'circle') {
          complexityFactor = 0.15; // 15% waste for circular rooms
          shapeComplexitySurcharge = area * 12; // Higher surcharge for circular cutting
        } else if (room.shapeType === 'triangle') {
          complexityFactor = 0.12; // 12% waste for triangular rooms
          shapeComplexitySurcharge = area * 10; // Surcharge for triangular cutting
        } else if (room.originalShape?.lineData?.some(line => line && line.isCurveMode)) {
          complexityFactor = 0.08; // 8% waste for curved rectangles
          shapeComplexitySurcharge = area * 6; // Curved cutting surcharge
        }
      }
      
      const finalArea = bestOption.totalArea * (1 + installationWaste + totalDoorAdjustments + complexityFactor);
      
      // Professional cost breakdown with shape-specific adjustments
      const carpetCostPerSqm = bestOption.costPerSqm;
      let installationCostPerSqm = room.type === 'merged' ? 15 : 12; // Higher for complex rooms
      
      // Adjust installation cost based on shape complexity
      if (room.carpetComplexity === 'complex' || room.carpetComplexity === 'very-complex') {
        installationCostPerSqm += 8; // Additional $8/sqm for complex shapes
      }
      if (room.shapeType === 'circle') {
        installationCostPerSqm += 10; // Extra $10/sqm for circular rooms
      }
      
      const seamingCostPerMeter = 8;
      const transitionCostPerMeter = 12; // For doorways
      const complexitySurcharge = room.type === 'merged' ? area * 5 : 0;

      const carpetCost = finalArea * carpetCostPerSqm;
      const installationCost = area * installationCostPerSqm;
      const seamingCost = bestOption.seamLength * seamingCostPerMeter;
      const transitionCost = (room.type === 'merged' ? room.complexity * 2 : 1) * transitionCostPerMeter;
      
      // Enhanced door-based transition costs
      const doorTransitionCost = doorAnalysis.doorCount > 0 ? 
        doorAnalysis.doorShapes.reduce((cost, door) => {
          const doorRadius = door.radius || 50;
          const doorCircumference = Math.PI * (doorRadius / 100); // Half circle perimeter in meters
          return cost + (doorCircumference * transitionCostPerMeter);
        }, 0) : 0;
      
      const totalTransitionCost = transitionCost + doorTransitionCost;
      const totalCost = carpetCost + installationCost + seamingCost + totalTransitionCost + complexitySurcharge + shapeComplexitySurcharge;

      // Generate cutting diagram data with shape-specific considerations
      const cuttingPlan = generateAdvancedCuttingPlan(room, bestOption);

      return {
        ...room,
        estimate: {
          ...bestOption,
          finalArea,
          needsSeaming: bestOption.seamCount > 0,
          installationFactors: {
            installationWaste: (installationWaste * 100).toFixed(1) + '%',
            doorAdjustments: (totalDoorAdjustments * 100).toFixed(1) + '%',
            complexityFactor: (complexityFactor * 100).toFixed(1) + '%',
            shapeComplexity: room.carpetComplexity,
            doorCount: doorAnalysis.doorCount,
            doorComplexity: doorAnalysis.hasComplexDoorCuts ? 'Complex' : 'Standard'
          },
          costs: {
            carpet: carpetCost,
            installation: installationCost,
            seaming: seamingCost,
            transitions: totalTransitionCost,
            complexity: complexitySurcharge,
            shapeComplexity: shapeComplexitySurcharge,
            total: totalCost
          },
          cuttingPlan,
          specialInstructions: generateSpecialInstructions(room)
        }
      };
    });

    // Calculate totals
    const totals = estimates.reduce((acc, room) => {
      acc.area += parseFloat(room.area);
      acc.carpetArea += room.estimate.finalArea;
      acc.cost += room.estimate.costs.total;
      acc.seamLength += room.estimate.seamLength;
      acc.rollsTotal += room.estimate.rollsNeeded;
      return acc;
    }, { area: 0, carpetArea: 0, cost: 0, seamLength: 0, rollsTotal: 0 });

    return { estimates, totals };
  };

  // Generate advanced cutting plan for any room shape
  const generateAdvancedCuttingPlan = (room, option) => {
    const cuts = [];
    const roomWidth = parseFloat(room.width);
    const roomHeight = parseFloat(room.height);

    // Handle different room types
    let cuttingStrategy = 'standard';
    let specialNotes = [];

    if (room.shapeType === 'circle') {
      cuttingStrategy = 'radial_template';
      specialNotes.push('Requires circular template cutting');
      specialNotes.push('Multiple seams radiating from center');
      specialNotes.push('Consider pattern matching for directional carpets');
    } else if (room.shapeType === 'triangle') {
      cuttingStrategy = 'template_cutting';
      specialNotes.push('Requires triangular template');
      specialNotes.push('Significant waste due to shape');
      specialNotes.push('Consider using remnants for smaller areas');
    } else if (room.originalShape?.lineData?.some(line => line && line.isCurveMode)) {
      cuttingStrategy = 'curved_template';
      specialNotes.push('Requires custom template for curved edges');
      specialNotes.push('Hand-cutting required for curves');
      specialNotes.push('Extra time for precision fitting');
    }

    if (option.rollsNeeded === 1 && cuttingStrategy === 'standard') {
      // Single piece - standard rectangular room
      cuts.push({
        id: 1,
        label: `Full piece - ${room.name}`,
        width: option.rollWidth,
        length: option.lengthNeeded,
        area: option.rollWidth * option.lengthNeeded,
        waste: option.wasteArea,
        position: { x: 0, y: 0 },
        cuttingMethod: 'straight_cut'
      });
    } else if (cuttingStrategy !== 'standard') {
      // Non-rectangular shapes require special handling
      cuts.push({
        id: 1,
        label: `Template piece - ${room.name}`,
        width: option.rollWidth,
        length: option.lengthNeeded,
        area: option.rollWidth * option.lengthNeeded,
        waste: option.wasteArea,
        position: { x: 0, y: 0 },
        cuttingMethod: cuttingStrategy,
        templateRequired: true,
        shapeType: room.shapeType
      });
    } else {
      // Multiple pieces with seams - rectangular rooms
      for (let i = 0; i < option.rollsNeeded; i++) {
        const pieceWidth = i === option.rollsNeeded - 1 
          ? roomWidth - (i * option.rollWidth) 
          : option.rollWidth;
        
        cuts.push({
          id: i + 1,
          label: `Piece ${i + 1} - ${room.name}`,
          width: option.rollWidth,
          length: option.lengthNeeded,
          area: option.rollWidth * option.lengthNeeded,
          actualWidth: pieceWidth,
          waste: (option.rollWidth - pieceWidth) * option.lengthNeeded,
          position: { x: i * option.rollWidth, y: 0 },
          seam: i > 0 ? { side: 'left', length: option.lengthNeeded } : null,
          cuttingMethod: 'straight_cut'
        });
      }
    }

    return {
      cuts,
      totalRolls: option.rollsNeeded,
      totalLength: option.lengthNeeded * option.rollsNeeded,
      totalWaste: option.wasteArea,
      rollWidth: option.rollWidth,
      orientation: option.orientation,
      pileDirection: option.pileDirection,
      cuttingStrategy,
      specialNotes,
      estimatedCuttingTime: calculateCuttingTime(room, cuts)
    };
  };

  // Generate special instructions based on room characteristics
  const generateSpecialInstructions = (room) => {
    const instructions = [];

    // Door-specific instructions
    const doorAnalysis = analyzeDoorShapes(room, shapes);
    
    if (doorAnalysis.doorCount > 0) {
      instructions.push('🚪 Door Installation Notes:');
      instructions.push(`• Found ${doorAnalysis.doorCount} door(s) in this room`);
      
      if (doorAnalysis.hasSnappedDoors) {
        instructions.push(`• ${doorAnalysis.snappedDoorsCount} door(s) are snapped to room walls`);
        instructions.push('• Snapped doors automatically oriented for room entry');
      }
      
      doorAnalysis.doorShapes.forEach((door, index) => {
        const radius = door.radius || 50;
        const diameter = (radius * 2) / 100; // Convert to meters
        const isSnapped = door.snappedToRoom ? ' (snapped)' : '';
        instructions.push(`• Door ${index + 1}: ${diameter.toFixed(2)}m diameter semi-circle${isSnapped}`);
        
        if (radius > 80) {
          instructions.push(`  - Large door requires template cutting`);
          instructions.push(`  - Allow extra material for complex cuts`);
        }
        
        if (door.snappedToRoom) {
          instructions.push(`  - Pre-positioned against room wall`);
          instructions.push(`  - Rotation optimized for entry direction`);
        }
        
        instructions.push(`  - Install transition strip around door perimeter`);
        instructions.push(`  - Cut carpet to follow door swing arc`);
      });
      
      if (doorAnalysis.hasComplexDoorCuts) {
        instructions.push('• Use professional door template system');
        instructions.push('• Allow 25% extra time for door cuts');
      }
    }

    if (room.shapeType === 'circle') {
      instructions.push('🔄 Circular Room Installation:');
      instructions.push('• Create cardboard template before cutting');
      instructions.push('• Cut carpet 2-3cm larger than template');
      instructions.push('• Use sharp utility knife for precise curves');
      instructions.push('• Install from center outward');
      instructions.push('• Trim excess carefully after installation');
    } else if (room.shapeType === 'triangle') {
      instructions.push('🔺 Triangular Room Installation:');
      instructions.push('• Create full-size template');
      instructions.push('• Allow 5cm extra on all sides');
      instructions.push('• Consider carpet grain direction');
      instructions.push('• Pre-stretch before final trimming');
    } else if (room.originalShape?.lineData?.some(line => line && line.isCurveMode)) {
      instructions.push('🌊 Curved Edge Installation:');
      instructions.push('• Create templates for each curved section');
      instructions.push('• Hand-cut curves with sharp blade');
      instructions.push('• Allow extra material for adjustments');
      instructions.push('• Use seaming tape for curved joints');
    }

    if (room.type === 'merged' && room.hasNonRectangular) {
      instructions.push('🔧 Complex Room Notes:');
      instructions.push('• Install rectangular sections first');
      instructions.push('• Template curved/shaped areas separately');
      instructions.push('• Plan seam placement carefully');
      instructions.push('• Consider hiring specialist installer');
    }

    return instructions;
  };

  // Calculate estimated cutting time
  const calculateCuttingTime = (room, cuts) => {
    let baseTime = cuts.length * 15; // 15 minutes per piece

    if (room.shapeType === 'circle') baseTime *= 2.5; // Circular rooms take much longer
    if (room.shapeType === 'triangle') baseTime *= 2; // Triangular rooms take longer
    if (room.originalShape?.lineData?.some(line => line && line.isCurveMode)) {
      baseTime *= 1.8; // Curved edges take longer
    }

    return `${Math.round(baseTime)} minutes`;
  };

  // Create carpet cutting visualization
  const createCuttingDiagram = (estimates) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Set canvas size
    canvas.width = 800;
    canvas.height = 600;
    
    // Background
    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Title
    ctx.fillStyle = '#2c3e50';
    ctx.font = 'bold 24px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Professional Carpet Cutting Plan', canvas.width / 2, 30);
    
    let yOffset = 60;
    const maxWidth = canvas.width - 40;
    
    estimates.forEach((room, roomIndex) => {
      const plan = room.estimate.cuttingPlan;
      
      // Room header with shape indicator
      ctx.fillStyle = getShapeColor(room.shapeType);
      ctx.font = 'bold 16px Arial';
      ctx.textAlign = 'left';
      const shapeIcon = getShapeIcon(room.shapeType);
      const displayName = (room.shapeType === 'rectangle' || (room.name && room.name.toLowerCase().startsWith('rectangle'))) ? `Room${room.name.replace(/^[^\d]*/, ' ')}` : room.name;
      ctx.fillText(`${shapeIcon} ${displayName} - ${plan.orientation}`, 20, yOffset);
      yOffset += 25;
      
      // Cutting strategy and pile direction
      ctx.fillStyle = '#6c757d';
      ctx.font = '12px Arial';
      ctx.fillText(`Strategy: ${plan.cuttingStrategy.replace('_', ' ')} | Pile: ${plan.pileDirection}`, 20, yOffset);
      yOffset += 20;
      
      // Special notes for complex shapes
      if (plan.specialNotes && plan.specialNotes.length > 0) {
        ctx.fillStyle = '#dc3545';
        ctx.font = 'italic 11px Arial';
        ctx.fillText(`⚠️ ${plan.specialNotes[0]}`, 20, yOffset);
        yOffset += 15;
      }
      
      // Calculate scale for this room
      const maxPieceWidth = Math.max(...plan.cuts.map(cut => cut.width));
      const scale = Math.min(maxWidth / maxPieceWidth, 80);
      
      plan.cuts.forEach((cut, index) => {
        const rectWidth = cut.width * scale;
        const rectHeight = 40;
        const x = 20 + cut.position.x * scale;
        const y = yOffset;
        
        // Choose color based on cutting method and waste
        let fillColor = '#28a745'; // Green for standard cuts
        if (cut.templateRequired) fillColor = '#ffc107'; // Yellow for template cuts
        if (cut.waste > cut.area * 0.3) fillColor = '#dc3545'; // Red for high waste
        if (cut.cuttingMethod === 'radial_template') fillColor = '#6f42c1'; // Purple for radial
        if (cut.cuttingMethod === 'curved_template') fillColor = '#17a2b8'; // Teal for curved
        
        // Draw carpet piece with special styling for complex cuts
        ctx.fillStyle = fillColor;
        if (cut.templateRequired) {
          // Draw with pattern for template cuts
          ctx.fillRect(x, y, rectWidth, rectHeight);
          
          // Add pattern overlay
          ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
          for (let i = 0; i < rectWidth; i += 8) {
            ctx.fillRect(x + i, y, 2, rectHeight);
          }
        } else {
          ctx.fillRect(x, y, rectWidth, rectHeight);
        }
        
        // Enhanced border with cutting method indication
        ctx.strokeStyle = '#343a40';
        ctx.lineWidth = cut.templateRequired ? 3 : 2;
        if (cut.cuttingMethod !== 'straight_cut') {
          ctx.setLineDash([5, 3]); // Dashed for special cuts
        }
        ctx.strokeRect(x, y, rectWidth, rectHeight);
        ctx.setLineDash([]); // Reset dash
        
        // Labels with enhanced information
        ctx.fillStyle = '#000';
        ctx.font = '10px Arial';
        ctx.textAlign = 'center';
        
        // Main label
        ctx.fillText(cut.label, x + rectWidth / 2, y + rectHeight / 2 - 10);
        
        // Dimensions
        ctx.fillText(`${cut.width.toFixed(2)}m × ${cut.length.toFixed(2)}m`, x + rectWidth / 2, y + rectHeight / 2);
        
        // Cutting method if special
        if (cut.cuttingMethod !== 'straight_cut') {
          ctx.fillStyle = '#fff';
          ctx.font = 'bold 9px Arial';
          ctx.fillText(cut.cuttingMethod.replace('_', ' ').toUpperCase(), x + rectWidth / 2, y + rectHeight / 2 + 12);
          ctx.fillStyle = '#000';
          ctx.font = '10px Arial';
        }
        
        // Waste information
        if (cut.waste > 0) {
          ctx.fillStyle = cut.waste > cut.area * 0.2 ? '#dc3545' : '#ffc107';
          ctx.fillText(`Waste: ${cut.waste.toFixed(2)}m²`, x + rectWidth / 2, y + rectHeight + 15);
        }
        
        // Template indicator
        if (cut.templateRequired) {
          ctx.fillStyle = '#6f42c1';
          ctx.font = 'bold 8px Arial';
          ctx.fillText('TEMPLATE REQUIRED', x + rectWidth / 2, y - 5);
        }
        
        // Seam indicator with enhanced styling
        if (cut.seam) {
          ctx.strokeStyle = '#dc3545';
          ctx.lineWidth = 4;
          ctx.setLineDash([8, 4]);
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x, y + rectHeight);
          ctx.stroke();
          ctx.setLineDash([]);
          
          // Seam label
          ctx.fillStyle = '#dc3545';
          ctx.font = 'bold 8px Arial';
          ctx.save();
          ctx.translate(x - 10, y + rectHeight / 2);
          ctx.rotate(-Math.PI / 2);
          ctx.fillText('SEAM', 0, 0);
          ctx.restore();
        }
      });
      
      yOffset += 60;
      
      // Enhanced room summary with complexity info
      ctx.fillStyle = '#6c757d';
      ctx.font = '11px Arial';
      ctx.textAlign = 'left';
      let summaryText = `Rolls: ${plan.totalRolls} × ${plan.totalLength.toFixed(2)}m | Waste: ${plan.totalWaste.toFixed(2)}m²`;
      if (plan.estimatedCuttingTime) {
        summaryText += ` | Time: ${plan.estimatedCuttingTime}`;
      }
      ctx.fillText(summaryText, 20, yOffset);
      yOffset += 25;
      
      // Add spacing between rooms
      yOffset += 10;
    });
    
    // Legend for colors and symbols
    yOffset += 20;
    ctx.fillStyle = '#2c3e50';
    ctx.font = 'bold 14px Arial';
    ctx.fillText('Legend:', 20, yOffset);
    yOffset += 20;
    
    const legendItems = [
      { color: '#28a745', text: 'Standard Cut', pattern: false },
      { color: '#ffc107', text: 'Template Required', pattern: true },
      { color: '#6f42c1', text: 'Radial/Circular Cut', pattern: false },
      { color: '#17a2b8', text: 'Curved Template', pattern: false },
      { color: '#dc3545', text: 'High Waste (>30%)', pattern: false }
    ];
    
    legendItems.forEach((item, index) => {
      const x = 20 + (index % 3) * 180;
      const y = yOffset + Math.floor(index / 3) * 25;
      
      // Legend color box
      ctx.fillStyle = item.color;
      ctx.fillRect(x, y - 10, 15, 15);
      
      if (item.pattern) {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
        for (let i = 0; i < 15; i += 4) {
          ctx.fillRect(x + i, y - 10, 1, 15);
        }
      }
      
      ctx.strokeStyle = '#343a40';
      ctx.lineWidth = 1;
      ctx.strokeRect(x, y - 10, 15, 15);
      
      // Legend text
      ctx.fillStyle = '#2c3e50';
      ctx.font = '10px Arial';
      ctx.textAlign = 'left';
      ctx.fillText(item.text, x + 20, y);
    });
    
    return canvas.toDataURL('image/png');
  };

  // Helper functions for shape visualization
  const getShapeColor = (shapeType) => {
    switch (shapeType) {
      case 'circle': return '#6f42c1';
      case 'triangle': return '#fd7e14';
      case 'rectangle': return '#007bff';
      case 'merged': return '#28a745';
      default: return '#6c757d';
    }
  };

  const getShapeIcon = (shapeType) => {
    switch (shapeType) {
      case 'circle': return '🔵';
      case 'triangle': return '🔺';
      case 'rectangle': return '🔲';
      default: return '🔲';
    }
  };

  // Helper function to get room icon
  const getRoomIcon = (room) => {
    if (room.shapeTypes?.includes('circle')) return '🔵';
    if (room.shapeTypes?.includes('triangle')) return '🔺';
    if (room.complexity?.level > 2) return '🏢';
    if (room.type === 'merged') return '🏢';
    return '🔲';
  };

  const getComplexityLevel = (level) => {
    switch (level) {
      case 1: return 'Standard';
      case 2: return 'Moderate';
      case 3: return 'Complex';
      default: return 'Standard';
    }
  };

  const { estimates, totals } = calculateCarpetEstimate();
  
  // Generate cutting diagram
  const [cuttingDiagramUrl, setCuttingDiagramUrl] = useState('');
  const [activeTab, setActiveTab] = useState('floorplan');
  
  useEffect(() => {
    if (estimates.length > 0) {
      const diagram = createCuttingDiagram(estimates);
      setCuttingDiagramUrl(diagram);
    }
  }, [estimates]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    // Create a comprehensive report with canvas image
    const reportData = {
      timestamp: new Date().toISOString(),
      projectSummary: {
        totalRooms: rooms.length,
        totalFloorArea: totals.area.toFixed(2),
        totalCarpetRequired: totals.carpetArea.toFixed(2),
        totalCost: totals.cost.toFixed(2)
      },
      rooms: estimates,
      totals,
      canvasImage: canvasDataUrl,
      metadata: {
        generatedBy: 'Floor Planner Pro',
        version: '1.0.0',
        exportFormat: 'JSON with embedded image'
      }
    };
    
    const dataStr = JSON.stringify(reportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `carpet-estimate-${new Date().toISOString().split('T')[0]}-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadFloorPlan = () => {
    if (canvasDataUrl) {
      const link = document.createElement('a');
      link.href = canvasDataUrl;
      link.download = `floor-plan-${new Date().toISOString().split('T')[0]}.png`;
      link.click();
    }
  };

  const handleDownloadCuttingPlan = () => {
    if (cuttingDiagramUrl) {
      const link = document.createElement('a');
      link.href = cuttingDiagramUrl;
      link.download = `cutting-plan-${new Date().toISOString().split('T')[0]}.png`;
      link.click();
    }
  };

  const modalContent = (
    <div className="estimate-modal-overlay">
      <style jsx>{`
        .estimate-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(circle at 20% 30%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, rgba(255, 119, 198, 0.3) 0%, transparent 50%),
            radial-gradient(circle at 40% 80%, rgba(120, 219, 255, 0.2) 0%, transparent 50%),
            linear-gradient(135deg, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.95) 100%);
          backdrop-filter: blur(20px);
          z-index: 10000;
          font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: modalFadeIn 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }

        @keyframes modalFadeIn {
          from {
            opacity: 0;
            backdrop-filter: blur(0px);
          }
          to {
            opacity: 1;
            backdrop-filter: blur(20px);
          }
        }

        .estimate-modal {
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.95) 0%, 
            rgba(255, 255, 255, 0.9) 50%, 
            rgba(248, 249, 250, 0.95) 100%);
          backdrop-filter: blur(40px);
          width: 95vw;
      
          height: 90vh;
          border-radius: 24px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          box-shadow: 
            0 32px 64px rgba(0, 0, 0, 0.2),
            0 16px 32px rgba(0, 0, 0, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.8),
            inset 0 -1px 0 rgba(255, 255, 255, 0.4);
          border: 1px solid rgba(255, 255, 255, 0.3);
          animation: modalSlideIn 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }

        @keyframes modalSlideIn {
          from {
            transform: translateY(50px) scale(0.9);
            opacity: 0;
          }
          to {
            transform: translateY(0) scale(1);
            opacity: 1;
          }
        }

        .modal-header {
          background: linear-gradient(135deg, #1a1a1a 0%, #404040 100%);
          color: white;
          padding: 28px 36px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-shrink: 0;
          position: relative;
          overflow: hidden;
        }

        .modal-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.2) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
          pointer-events: none;
        }

        .header-content {
          position: relative;
          z-index: 2;
        }

        .modal-title {
          font-size: 32px;
          font-weight: 800;
          margin: 0 0 8px 0;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          letter-spacing: -0.8px;
          background: linear-gradient(45deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 1));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .modal-subtitle {
          font-size: 16px;
          opacity: 0.9;
          margin: 0;
          color:white;
          font-weight: 400;
          letter-spacing: 0.2px;
        }

        .modal-actions {
          display: flex;
          gap: 16px;
          position: relative;
          z-index: 2;
        }

        .header-btn {
          padding: 12px 20px;
          border: none;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          backdrop-filter: blur(10px);
          position: relative;
          overflow: hidden;
        }

        .header-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(45deg, rgba(255, 255, 255, 0.2) 0%, transparent 50%);
          transform: translateX(-100%);
          transition: transform 0.6s ease;
        }

        .header-btn:hover::before {
          transform: translateX(100%);
        }

        .btn-secondary {
          background: rgba(255, 255, 255, 0.2);
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.3);
        }

        .btn-secondary:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: translateY(-2px);
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
        }

        .btn-close {
          background: rgba(255, 255, 255, 0.1);
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.3);
          width: 44px;
          height: 44px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          font-weight: 400;
        }

        .btn-close:hover {
          background: rgba(255, 255, 255, 0.2);
          transform: rotate(90deg) scale(1.1);
        }

        .modal-content {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .content-header {
          padding: 24px 36px 0 36px;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(248, 249, 250, 0.6));
          border-bottom: 1px solid rgba(226, 232, 240, 0.8);
        }

        .project-summary {
          background: linear-gradient(135deg, #2d3748 0%, #4a5568 100%);
          color: white;
          border-radius: 20px;
          padding: 24px;
          margin-bottom: 24px;
          position: relative;
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .project-summary::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 70% 70%, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
          pointer-events: none;
        }

        .summary-content {
          position: relative;
          z-index: 2;
          padding:20px;
        }

        .summary-title {
          font-size: 24px;
          font-weight: 700;
          margin-bottom: 20px;
          text-align: center;
          color:whitesmoke;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .summary-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
        }

        .summary-item {
          text-align: center;
          padding: 20px;
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(10px);
          border-radius: 16px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          transition: transform 0.3s ease;
        }

        .summary-item:hover {
          transform: translateY(-4px);
          background: rgba(255, 255, 255, 0.2);
        }

        .summary-value {
          font-size: 32px;
          font-weight: 900;
          margin-bottom: 8px;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .summary-label {
          font-size: 14px;
          opacity: 0.9;
          text-transform: uppercase;
          letter-spacing: 1px;
          font-weight: 600;
        }

        .content-grid {
          display: grid;
          grid-template-columns: repeat(2,1fr);
          gap: 32px;
          height: 100%;
          padding: 0 36px 36px 36px;
        }

        .estimates-section {
          overflow-y: auto;
          padding-right: 16px;
          padding-top:20px;
        }

        .estimates-section::-webkit-scrollbar {
          width: 8px;
        }

        .estimates-section::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.05);
          border-radius: 4px;
        }

        .estimates-section::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #1a1a1a, #404040);
          border-radius: 4px;
        }

        .estimates-section::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(135deg, #0d0d0d, #2a2a2a);
        }

        .canvas-section {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 249, 250, 0.8));
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 24px;
          display: flex;
          flex-direction: column;
          
          overflow-y:scroll;
         padding-bottom:20px;
          border: 1px solid rgba(255, 255, 255, 0.6);
          box-shadow: 
            0 16px 32px rgba(0, 0, 0, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.8);
        }

        .image-tabs {
          display: flex;
          gap: 6px;
          margin-bottom: 20px;
          background: rgba(255, 255, 255, 0.7);
          padding: 6px;
          border-radius: 14px;
          backdrop-filter: blur(10px);
        }

        .tab-button {
          padding: 10px 16px;
          border: none;
          background: transparent;
          color: #64748b;
          border-radius: 10px;
          cursor: pointer;
          font-weight: 600;
          font-size: 13px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          flex: 1;
          position: relative;
          overflow: hidden;
          text-align: center;
        }

        .tab-button.active {
          background: linear-gradient(135deg, #1a1a1a, #404040);
          color: white;
          box-shadow: 0 4px 12px rgba(26, 26, 26, 0.4);
        }

        .tab-button:hover:not(.active) {
          background: rgba(26, 26, 26, 0.1);
          color: #1a1a1a;
        }

        .canvas-title {
          font-size: 20px;
          font-weight: 700;
          color: #2d3748;
          margin-bottom: 16px;
          text-align: center;
          padding: 16px;
          background: rgba(255, 255, 255, 0.8);
          border-radius: 12px;
          backdrop-filter: blur(10px);
        }

        .canvas-image-container {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 249, 250, 0.8));
          border-radius: 16px;
          padding: 20px;
          box-shadow: 
            inset 0 2px 4px rgba(0, 0, 0, 0.06),
            0 1px 3px rgba(0, 0, 0, 0.1);
          min-height: 400px;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.8);
          position: relative;
        }

        .image-download-btn {
          position: absolute;
          top: 16px;
          right: 16px;
          background: linear-gradient(135deg, #1a1a1a, #404040);
          color: white;
          border: none;
          border-radius: 12px;
          padding: 8px 12px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          backdrop-filter: blur(10px);
          z-index: 10;
        }

        .image-download-btn:hover {
          background: linear-gradient(135deg, #0d0d0d, #2a2a2a);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(26, 26, 26, 0.4);
        }

        .canvas-image {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
          border-radius: 12px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
        }

        .canvas-placeholder {
          color: #64748b;
          font-size: 18px;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }

        .placeholder-icon {
          font-size: 48px;
          opacity: 0.6;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 0.9; }
        }

        .canvas-info {
          margin-top: 16px;
          font-size: 12px;
          color: #64748b;
          text-align: center;
          padding: 12px;
          background: rgba(255, 255, 255, 0.6);
          border-radius: 8px;
          backdrop-filter: blur(10px);
        }

        .room-estimate-card {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 249, 250, 0.9));
          backdrop-filter: blur(20px);
          border-radius: 20px;
          overflow: hidden;
          margin-bottom: 24px;
          border: 1px solid rgba(255, 255, 255, 0.6);
          box-shadow: 
            0 12px 24px rgba(0, 0, 0, 0.08),
            inset 0 1px 0 rgba(255, 255, 255, 0.9);
          position: relative;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .room-estimate-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #1a1a1a, #404040, #606060, #808080);
          background-size: 300% 100%;
          animation: cardShimmer 4s ease-in-out infinite;
        }

        @keyframes cardShimmer {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        .room-estimate-card:hover {
          transform: translateY(-4px);
          box-shadow: 
            0 20px 40px rgba(0, 0, 0, 0.12),
            inset 0 1px 0 rgba(255, 255, 255, 1);
        }

        .room-card-header {
          padding: 24px 28px;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(248, 249, 250, 0.6));
        }

        .room-name {
          font-size: 20px;
          font-weight: 700;
          color: #2d3748;
          margin: 0 0 12px 0;
          letter-spacing: -0.5px;
        }

        .room-dimensions {
          color: #64748b;
          font-size: 14px;
          font-weight: 500;
        }

        .complexity-badge {
          background: linear-gradient(135deg, #ff6b6b, #ee5a24);
          color: white;
          font-size: 11px;
          padding: 6px 12px;
          border-radius: 20px;
          margin-left: 12px;
          font-weight: 600;
          display: inline-block;
          box-shadow: 0 2px 8px rgba(255, 107, 107, 0.4);
        }

        .shape-types {
          font-size: 12px;
          color: #64748b;
          margin-top: 6px;
          font-style: italic;
          opacity: 0.8;
        }

        .estimate-details {
          padding: 28px;
        }

        .detail-section {
          margin-bottom: 32px;
        }

        .detail-title {
          font-size: 18px;
          font-weight: 700;
          color: #2d3748;
          margin-bottom: 20px;
          padding: 16px 20px;
          background: linear-gradient(135deg, rgba(26, 26, 26, 0.1), rgba(64, 64, 64, 0.1));
          border-radius: 12px;
          border-left: 4px solid #1a1a1a;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .detail-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 16px;
        }

        .detail-item {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 249, 250, 0.8));
          backdrop-filter: blur(10px);
          padding: 16px 20px;
          border-radius: 12px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border: 1px solid rgba(255, 255, 255, 0.6);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
          transition: all 0.3s ease;
        }

        .detail-item:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
        }

        .detail-label {
          color: #64748b;
          font-size: 14px;
          font-weight: 500;
        }

        .detail-value {
          color: #2d3748;
          font-weight: 700;
          font-size: 14px;
        }

        .roll-option-card {
          background: linear-gradient(135deg, rgba(79, 172, 254, 0.1), rgba(0, 242, 254, 0.1));
          border-radius: 16px;
          padding: 20px;
          border: 1px solid rgba(79, 172, 254, 0.3);
          backdrop-filter: blur(10px);
        }

        .roll-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid rgba(79, 172, 254, 0.2);
        }

        .roll-type {
          font-size: 16px;
          font-weight: 700;
          color: #2d3748;
        }

        .roll-orientation {
          background: linear-gradient(135deg, #4facfe, #00f2fe);
          color: white;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
        }

        .roll-details {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 12px;
        }

        .roll-metric {
          display: flex;
          justify-content: space-between;
          padding: 12px;
          background: rgba(255, 255, 255, 0.8);
          border-radius: 8px;
          font-size: 14px;
        }

        .roll-metric span:first-child {
          color: #64748b;
          font-weight: 500;
        }

        .roll-metric span:last-child {
          color: #2d3748;
          font-weight: 700;
        }

        .cost-breakdown {
          background: linear-gradient(135deg, #1a1a1a 0%, #404040 100%);
          color: white;
          border-radius: 16px;
          padding: 24px;
          position: relative;
          overflow: hidden;
        }

        .cost-breakdown::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 70% 70%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
          pointer-events: none;
        }

        .cost-title {
          font-size: 18px;
          font-weight: 700;
          margin-bottom: 20px;
          text-align: center;
          position: relative;
          z-index: 2;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .cost-item {
          display: flex;
          justify-content: space-between;
          padding: 12px 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
          position: relative;
          z-index: 2;
          font-size: 15px;
        }

        .cost-item:last-child {
          border-bottom: none;
        }

        .cost-item.total {
          border-top: 2px solid rgba(255, 255, 255, 0.4);
          font-weight: 700;
          font-size: 18px;
          margin-top: 12px;
          padding-top: 16px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          padding-left: 12px;
          padding-right: 12px;
        }

        .cutting-strategy {
          background: linear-gradient(135deg, #404040 0%, #1a1a1a 100%);
          color: white;
          border-radius: 16px;
          padding: 20px;
          margin-top: 24px;
          position: relative;
          overflow: hidden;
        }

        .cutting-strategy::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
          pointer-events: none;
        }

        .strategy-header {
          font-size: 16px;
          font-weight: 700;
          margin-bottom: 16px;
          position: relative;
          z-index: 2;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .strategy-notes {
          position: relative;
          z-index: 2;
        }

        .cutting-method {
          font-size: 14px;
          margin-bottom: 8px;
          padding: 8px 12px;
          background: rgba(255, 255, 255, 0.15);
          border-radius: 8px;
          backdrop-filter: blur(10px);
        }

        .cutting-time {
          background: rgba(255, 255, 255, 0.15);
          padding: 10px 12px;
          border-radius: 8px;
          margin: 12px 0;
          font-size: 13px;
          text-align: center;
          backdrop-filter: blur(10px);
        }

        .strategy-notes-list {
          margin-top: 12px;
        }

        .strategy-note {
          margin: 6px 0;
          padding-left: 8px;
          font-size: 13px;
          line-height: 1.4;
        }

        .special-instructions {
          background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
          color: white;
          border-radius: 16px;
          padding: 20px;
          position: relative;
          overflow: hidden;
        }

        .special-instructions::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 75% 75%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
          pointer-events: none;
        }

        .instructions-header {
          font-size: 16px;
          font-weight: 700;
          margin-bottom: 16px;
          position: relative;
          z-index: 2;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .instructions-content {
          position: relative;
          z-index: 2;
          font-size: 14px;
          line-height: 1.5;
        }

        .instruction-item {
          margin: 8px 0;
          padding-left: 12px;
        }

        .seaming-alert {
          background: linear-gradient(135deg, rgba(255, 193, 7, 0.9), rgba(255, 152, 0, 0.9));
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 193, 7, 0.4);
          border-radius: 12px;
          padding: 16px 20px;
          margin-top: 16px;
          color: #8b4513;
          font-weight: 500;
          font-size: 14px;
          box-shadow: 0 4px 12px rgba(255, 193, 7, 0.3);
        }

        .alert-icon {
          margin-right: 8px;
          font-size: 16px;
        }

        @media (max-width: 1200px) {
          .content-grid {
            grid-template-columns: 1fr;
            gap: 24px;
          }
          
          .canvas-section {
            order: -1;
            max-height: 400px;
          }
        }

        @media (max-width: 768px) {
          .estimate-modal {
            width: 98vw;
            height: 95vh;
            border-radius: 16px;
          }
          
          .modal-header {
            padding: 20px 24px;
          }
          
          .modal-title {
            font-size: 24px;
          }
          
          .content-grid {
            padding: 0 24px 24px 24px;
          }
        }

        .cutting-comparison-container {
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .comparison-header {
          text-align: center;
          padding: 16px;
          background: linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1));
          border-radius: 12px;
          border: 1px solid rgba(102, 126, 234, 0.2);
        }

        .comparison-header h3 {
          margin: 0 0 8px 0;
          font-size: 18px;
          font-weight: 700;
          color: #2d3748;
        }

        .comparison-header p {
          margin: 0;
          font-size: 14px;
          color: #64748b;
        }

        .comparison-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          flex: 1;
        }

        .comparison-panel {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 249, 250, 0.8));
          border-radius: 16px;
          padding: 16px;
          border: 1px solid rgba(255, 255, 255, 0.6);
          backdrop-filter: blur(10px);
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .panel-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-bottom: 12px;
          border-bottom: 1px solid rgba(226, 232, 240, 0.8);
        }

        .panel-header h4 {
          margin: 0;
          font-size: 16px;
          font-weight: 700;
          color: #2d3748;
        }

        .panel-badge {
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .panel-badge.original {
          background: linear-gradient(135deg, #2a2a2a, #606060);
          color: white;
        }

        .panel-badge.optimized {
          background: linear-gradient(135deg, #404040, #1a1a1a);
          color: white;
        }

        .image-container {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.8);
          border-radius: 12px;
          padding: 12px;
          min-height: 250px;
          border: 1px solid rgba(226, 232, 240, 0.6);
          position: relative;
        }

        .comparison-download-btn {
          position: absolute;
          top: 8px;
          right: 8px;
          background: linear-gradient(135deg, #404040, #1a1a1a);
          color: white;
          border: none;
          border-radius: 8px;
          padding: 6px 8px;
          font-size: 11px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          opacity: 0.9;
          z-index: 10;
        }

        .comparison-download-btn:hover {
          background: linear-gradient(135deg, #2a2a2a, #0d0d0d);
          transform: translateY(-1px);
          opacity: 1;
        }

        .comparison-image {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .image-placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
          color: #64748b;
          text-align: center;
        }

        .image-placeholder .placeholder-icon {
          font-size: 32px;
          opacity: 0.6;
        }

        .panel-info {
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 12px;
          background: rgba(255, 255, 255, 0.6);
          border-radius: 8px;
          backdrop-filter: blur(5px);
        }

        .info-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 14px;
        }

        .info-label {
          color: #64748b;
          font-weight: 500;
        }

        .info-value {
          color: #2d3748;
          font-weight: 700;
        }

        .comparison-insights {
          margin-top: 20px;
        }

        .insight-header {
          margin-bottom: 16px;
          text-align: center;
        }

        .insight-header h4 {
          margin: 0;
          font-size: 16px;
          font-weight: 700;
          color: #2d3748;
        }

        .insights-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }

        .insight-card {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 249, 250, 0.8));
          border-radius: 12px;
          padding: 16px;
          border: 1px solid rgba(255, 255, 255, 0.6);
          backdrop-filter: blur(10px);
          display: flex;
          gap: 12px;
          align-items: flex-start;
          transition: transform 0.3s ease;
        }

        .insight-card:hover {
          transform: translateY(-2px);
        }

        .insight-card.efficiency {
          border-left: 4px solid #2a2a2a;
        }

        .insight-card.cost {
          border-left: 4px solid #404040;
        }

        .insight-card.seaming {
          border-left: 4px solid #1a1a1a;
        }

        .insight-icon {
          font-size: 20px;
          opacity: 0.8;
        }

        .insight-content {
          flex: 1;
        }

        .insight-content h5 {
          margin: 0 0 4px 0;
          font-size: 14px;
          font-weight: 700;
          color: #2d3748;
        }

        .insight-content p {
          margin: 0 0 4px 0;
          font-size: 14px;
          font-weight: 600;
          color: #4a5568;
        }

        .insight-content small {
          font-size: 12px;
          color: #64748b;
          opacity: 0.8;
        }

        @media (max-width: 768px) {
          .comparison-grid {
            grid-template-columns: 1fr;
          }
          
          .insights-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="estimate-modal">
        <div className="modal-header">
          <div className="header-content">
            <h2 className="modal-title">🧮 Carpet Estimate Report</h2>
            <p className="modal-subtitle ">Project Summary</p>
          </div>
          <div className="modal-actions">
            <button className="header-btn btn-secondary" onClick={handlePrint}>
              🖨️ Print
            </button>
            <button className="header-btn btn-secondary" onClick={handleDownload}>
              💾 Download
            </button>
            <button className="btn-close" onClick={onClose}>
              ✕
            </button>
          </div>
        </div>

        <div className="modal-content">
          <div className="content-grid">
            <div className="estimates-section">
              <div className="project-summary">
                <div className="summary-content">
                  <div className="summary-title">Project Summary</div>
                  <div className="summary-grid">
                    <div className="summary-item">
                      <div className="summary-value">{rooms.length}</div>
                      <div className="summary-label">Total Rooms</div>
                    </div>
                    <div className="summary-item">
                      <div className="summary-value">{totals.area.toFixed(1)} m²</div>
                      <div className="summary-label">Floor Area</div>
                    </div>
                    <div className="summary-item">
                      <div className="summary-value">{totals.carpetArea.toFixed(1)} m²</div>
                      <div className="summary-label">Carpet Required</div>
                    </div>
                    
                  </div>
                </div>
              </div>

              <div className="room-estimates">
                {estimates.map((room) => (
                  <div key={room.id} className="room-estimate-card">
                    <div className="room-card-header">
                      <h3 className="room-name">
                        {getRoomIcon(room)} {room.name || `Room ${room.id.slice(-3)}`}
                        {room.complexity && room.complexity.level > 1 && (
                          <span className="complexity-badge">
                            {room.complexity.level === 2 ? 'MODERATE' : 'COMPLEX'}
                          </span>
                        )}
                      </h3>
                      <div className="room-dimensions">
                        📐 {parseFloat(room.width).toFixed(1)}m × {parseFloat(room.height).toFixed(1)}m
                        | 📦 Area: {parseFloat(room.area).toFixed(1)} m²
                      </div>
                      {room.shapeTypes && (
                        <div className="shape-types">
                          🎨 Shapes: {room.shapeTypes.join(', ')}
                        </div>
                      )}
                    </div>
                    
                    <div className="estimate-details">
                      <div className="detail-section">
                        <div className="detail-title">📏 Room Analysis</div>
                        <div className="detail-grid">
                          <div className="detail-item">
                            <span className="detail-label">Perimeter</span>
                            <span className="detail-value">{parseFloat(room.perimeter).toFixed(1)} m</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Area</span>
                            <span className="detail-value">{parseFloat(room.area).toFixed(1)} m²</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Shape Count</span>
                            <span className="detail-value">{room.shapeCount || 1}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">Complexity</span>
                            <span className="detail-value">
                              {room.complexity ? getComplexityLevel(room.complexity.level) : 'Standard'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {room.estimate && (
                        <div className="detail-section">
                          <div className="detail-title">🎯 Optimal Roll Configuration</div>
                          <div className="roll-option-card">
                            <div className="roll-header">
                              <span className="roll-type">📏 {room.estimate.rollName}</span>
                              <span className="roll-orientation">
                                {room.estimate.orientation === 'Rotated 90°' ? '🔄 Rotated' : '➡️ Standard'}
                              </span>
                            </div>
                            <div className="roll-details">
                              <div className="roll-metric">
                                <span>Required Length:</span>
                                <span>{room.estimate.lengthNeeded.toFixed(1)}m</span>
                              </div>
                              <div className="roll-metric">
                                <span>Waste Factor:</span>
                                <span>{room.estimate.wastePercentage.toFixed(0)}%</span>
                              </div>
                              <div className="roll-metric">
                                <span>Total with Waste:</span>
                                <span>{room.estimate.totalArea.toFixed(1)}m²</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="cost-breakdown">
                        <div className="cost-title">💰 Cost Breakdown</div>
                        <div className="cost-item">
                          <span>Material Cost</span>
                          <span>{room.estimate?.costs?.carpet?.toFixed(0) || '0'}</span>
                        </div>
                        <div className="cost-item">
                          <span>Installation</span>
                          <span>{room.estimate?.costs?.installation?.toFixed(0) || '0'}</span>
                        </div>
                        {room.estimate?.costs?.seaming > 0 && (
                          <div className="cost-item">
                            <span>Seaming</span>
                            <span>{room.estimate.costs.seaming.toFixed(0)}</span>
                          </div>
                        )}
                        {room.estimate?.costs?.complexity > 0 && (
                          <div className="cost-item">
                            <span>Complexity Surcharge</span>
                            <span>{room.estimate.costs.complexity.toFixed(0)}</span>
                          </div>
                        )}
                        {room.estimate?.costs?.shapeComplexity > 0 && (
                          <div className="cost-item">
                            <span>Shape Complexity</span>
                            <span>{room.estimate.costs.shapeComplexity.toFixed(0)}</span>
                          </div>
                        )}
                        <div className="cost-item total">
                          <span>Room Total</span>
                          <span>{room.estimate?.costs?.total?.toFixed(0) || '0'}</span>
                        </div>
                      </div>

                      {room.estimate && room.estimate.cuttingPlan && (
                        <div className="cutting-strategy">
                          <div className="strategy-header">
                            ✂️ Cutting Strategy: {room.estimate.cuttingPlan.cuttingStrategy.replace('_', ' ').toUpperCase()}
                          </div>
                          <div className="strategy-notes">
                            <div className="cutting-method">
                              Method: <strong>{room.estimate.cuttingPlan.cuttingStrategy}</strong>
                            </div>
                            <div className="cutting-time">
                              ⏱️ Estimated cutting time: {room.estimate.cuttingPlan.estimatedCuttingTime}
                            </div>
                            {room.estimate.cuttingPlan.specialNotes && room.estimate.cuttingPlan.specialNotes.length > 0 && (
                              <div className="strategy-notes-list">
                                {room.estimate.cuttingPlan.specialNotes.map((note, idx) => (
                                  <div key={idx} className="strategy-note">• {note}</div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {room.estimate && room.estimate.specialInstructions && room.estimate.specialInstructions.length > 0 && (
                        <div className="special-instructions">
                          <div className="instructions-header">
                            ⚠️ Special Installation Instructions
                          </div>
                          <div className="instructions-content">
                            {room.estimate.specialInstructions.map((instruction, idx) => (
                              <div key={idx} className="instruction-item">• {instruction}</div>
                            ))}
                          </div>
                        </div>
                      )}

                      {room.estimate && room.estimate.needsSeaming && (
                        <div className="seaming-alert">
                          <span className="alert-icon">⚠️</span>
                          <strong>Seaming Required:</strong> {room.estimate.seamCount} seam(s) totaling {room.estimate.seamLength.toFixed(1)}m
                          {room.type === 'merged' && (
                            <div>Complex room may require additional pattern matching at seams.</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="canvas-section">
              <div className="image-tabs">
                <button 
                  className={`tab-button ${activeTab === 'floorplan' ? 'active' : ''}`}
                  onClick={() => setActiveTab('floorplan')}
                >
                  📐 Floor Plan
                </button>
                <button 
                  className={`tab-button ${activeTab === 'cutting' ? 'active' : ''}`}
                  onClick={() => setActiveTab('cutting')}
                >
                  ✂️ Cutting Plan
                </button>
                <button 
                  className={`tab-button ${activeTab === 'comparison' ? 'active' : ''}`}
                  onClick={() => setActiveTab('comparison')}
                >
                  📊 View Cut-plan
                </button>
              </div>
              
              <div className="canvas-title">
                {activeTab === 'floorplan' ? '📐 Floor Plan' : 
                 activeTab === 'cutting' ? '✂️ Carpet Cutting Plan' : 
                 '📊 Plan Comparison & Analysis'}
              </div>
              
              <div className="canvas-image-container">
                {activeTab === 'floorplan' ? (
                  <>
                    {canvasDataUrl && (
                      <button className="image-download-btn" onClick={handleDownloadFloorPlan}>
                        💾 Download
                      </button>
                    )}
                    {canvasDataUrl ? (
                      <img 
                        src={canvasDataUrl} 
                        alt="Floor Plan" 
                        className="canvas-image"
                      />
                    ) : (
                      <div className="canvas-placeholder">
                        <div className="placeholder-icon">🎨</div>
                        Capturing floor plan...
                        <small>If this persists, try refreshing the page</small>
                      </div>
                    )}
                  </>
                ) : activeTab === 'cutting' ? (
                  <>
                    {cuttingDiagramUrl && (
                      <button className="image-download-btn" onClick={handleDownloadCuttingPlan}>
                        💾 Download
                      </button>
                    )}
                    {cuttingDiagramUrl ? (
                      <img 
                        src={cuttingDiagramUrl} 
                        alt="Carpet Cutting Plan" 
                        className="canvas-image"
                      />
                    ) : (
                      <div className="canvas-placeholder">
                        <div className="placeholder-icon">✂️</div>
                        Generating cutting plan...
                        <small>Professional carpet layout diagram</small>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="cutting-comparison-container">
                    <div className="comparison-header">
                      <h3>📊 Plan Comparison & Analysis</h3>
                      <p>Original floor plan vs. optimized cutting layout</p>
                    </div>
                    
                    <div className="comparison-grid">
                      <div className="comparison-panel">
                        <div className="panel-header">
                          <h4>🏠 Original Floor Plan</h4>
                          <span className="panel-badge original">ORIGINAL</span>
                        </div>
                        <div className="image-container">
                          {canvasDataUrl ? (
                            <>
                              <button className="comparison-download-btn" onClick={handleDownloadFloorPlan}>
                                💾
                              </button>
                              <img src={canvasDataUrl} alt="Original Floor Plan" className="comparison-image" />
                            </>
                          ) : (
                            <div className="image-placeholder">
                              <div className="placeholder-icon">🏠</div>
                              <span>Original Plan</span>
                            </div>
                          )}
                        </div>
                        <div className="panel-info">
                          <div className="info-item">
                            <span className="info-label">Total Rooms:</span>
                            <span className="info-value">{rooms.length}</span>
                          </div>
                          <div className="info-item">
                            <span className="info-label">Floor Area:</span>
                            <span className="info-value">{totals.area.toFixed(1)} m²</span>
                          </div>
                        </div>
                      </div>

                      <div className="comparison-panel">
                        <div className="panel-header">
                          <h4>✂️ Cutting Layout Plan</h4>
                          <span className="panel-badge optimized">OPTIMIZED</span>
                        </div>
                        <div className="image-container">
                          {cuttingDiagramUrl ? (
                            <>
                              <button className="comparison-download-btn" onClick={handleDownloadCuttingPlan}>
                                💾
                              </button>
                              <img src={cuttingDiagramUrl} alt="Cutting Layout Plan" className="comparison-image" />
                            </>
                          ) : (
                            <div className="image-placeholder">
                              <div className="placeholder-icon">✂️</div>
                              <span>Generating cutting plan...</span>
                            </div>
                          )}
                        </div>
                        <div className="panel-info">
                          <div className="info-item">
                            <span className="info-label">Total Rolls:</span>
                            <span className="info-value">{totals.rollsTotal}</span>
                          </div>
                          <div className="info-item">
                            <span className="info-label">Carpet Required:</span>
                            <span className="info-value">{totals.carpetArea.toFixed(1)} m²</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="comparison-insights">
                      <div className="insight-header">
                        <h4>💡 Cutting Insights & Recommendations</h4>
                      </div>
                      <div className="insights-grid">
                        <div className="insight-card efficiency">
                          <div className="insight-icon">📈</div>
                          <div className="insight-content">
                            <h5>Material Efficiency</h5>
                            <p>Waste Factor: {((totals.carpetArea - totals.area) / totals.area * 100).toFixed(1)}%</p>
                            <small>Industry standard: 10-15%</small>
                          </div>
                        </div>
                        
                        <div className="insight-card cost">
                          <div className="insight-icon">💰</div>
                          <div className="insight-content">
                            <h5>Cost Optimization</h5>
                            <p>Total Project: {totals.cost.toFixed(0)}</p>
                            <small>Includes all surcharges</small>
                          </div>
                        </div>
                        
                        <div className="insight-card seaming">
                          <div className="insight-icon">🔗</div>
                          <div className="insight-content">
                            <h5>Seaming Requirements</h5>
                            <p>Total Seams: {totals.seamLength.toFixed(1)}m</p>
                            <small>Professional installation recommended</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="canvas-info">
                {activeTab === 'floorplan' 
                  ? `Floor plan generated on ${new Date().toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}`
                  : activeTab === 'cutting'
                  ? `Total rolls needed: ${totals.rollsTotal} | Cutting plan optimized for minimal waste`
                  : `Comprehensive analysis comparing original design with optimized cutting layout`
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default EstimateModal;